import { DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, switchMap, tap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { DepositApi } from 'src/app/shared/apis/deposit.api';
import { WalletApi } from 'src/app/shared/apis/wallet.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent, ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { ThirdAuthM8Component } from 'src/app/shared/components/third-auth-m8/third-auth-m8.component';
import { ThirdAuthM8Service } from 'src/app/shared/components/third-auth-m8/third-auth-m8.service';
import { InputFloatDirective } from 'src/app/shared/directive/input.directive';
import { ThirdAuthUser, Web3WalletType } from 'src/app/shared/interfaces/auth.interface';
import { NetworksInterface, TokenNetworksInterface } from 'src/app/shared/interfaces/deposit.interface';
import { CurrencyBalance, ForbidDepositType, ForbidPaymentCategory } from 'src/app/shared/interfaces/wallet.interface';
import { CurrencyValuePipe } from 'src/app/shared/pipes/currency-value.pipe';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { Web3WalletNamePipe } from 'src/app/shared/pipes/web3-wallet-name.pipe';
import { Web3WalletService } from 'src/app/shared/service/web3-wallets/web3-wallet.service';
import { WalletDialogType, WalletService } from '../../wallet.service';
import { DepositBonusDropdownComponent } from '../deposit-bonus-dropdown/deposit-bonus-dropdown.component';
import { DepositWithdrawForbidComponent } from '../deposit-withdraw-forbid/deposit-withdraw-forbid.component';

@UntilDestroy()
@Component({
  selector: 'app-deposit-web3-wallet',
  standalone: true,
  imports: [
    DecimalPipe,
    CSButtonComponent,
    ThirdAuthM8Component,
    CustomizeFormModule,
    PipesModule,
    CSInputComponent,
    DepositBonusDropdownComponent,
    CSSelectComponent,
    InputFloatDirective,
    Web3WalletNamePipe,
    PipesModule,
    DepositWithdrawForbidComponent,
  ],
  templateUrl: './deposit-web3-wallet.component.html',
  styleUrl: './deposit-web3-wallet.component.scss',
})
export class DepositWeb3WalletComponent implements OnInit {
  constructor(
    private cdr: ChangeDetectorRef,
    public walletService: WalletService,
    public thirdAuthM8Service: ThirdAuthM8Service,
    private appService: AppService,
    private web3WalletService: Web3WalletService,
    private depositApi: DepositApi,
    private walletApi: WalletApi,
    private userAssetsService: UserAssetsService,
    private currencyValuePipe: CurrencyValuePipe,
    private deviceService: DeviceDetectorService,
  ) {}
  ThirdAuthUser = ThirdAuthUser;

  isConnected: boolean = false;
  isLoading: boolean = false;
  isBonusLoading: boolean = false;
  isAddressLoading: boolean = false;
  isWeb3WalletLoading: boolean = false;
  hasNetworkError: boolean = false;
  isTransacting: boolean = false;
  isTgApp: boolean = sessionStorage.getItem('isTgApp') === 'true'; // Only show TonConnect when using TG Mini App

  // 存款禁止加載狀態
  isForbidLoading: boolean = false;
  isDepositForbid: boolean = false;

  tokenNetworkList: TokenNetworksInterface[] = [];
  selectedCurrencyOption!: TokenNetworksInterface;
  currencyOptions: ICSOption<TokenNetworksInterface>[] = [];
  selectedNetworkOption: NetworksInterface | undefined;
  networkOptions: ICSOption<NetworksInterface>[] = [];
  userBalanceList: CurrencyBalance[] = [];

  amount: string = '';
  depositMaxAmount: string = '0';
  depositAddress: string = '';
  currentCurrency: string = 'USDT';
  currentDecimals: number = 6; // Default decimals for USDT
  currentBalance: number = 0;
  currentWeb3Wallet: Web3WalletType = Web3WalletType.MetaMask; // Default to MetaMask
  currentWeb3WalletBalance: string = '0';
  isHideWeb3Currency: boolean = false;

  /** 存款金額輸入驗證 */
  amountValid: boolean = true;
  /** 存款金額錯誤提示文字 */
  errText: string = '';

  async ngOnInit() {
    // 存款前先判断是否禁止存款
    await this.checkDepositForbid();

    this.isLoading = true;
    this.web3WalletService.isConnected$
      .pipe(
        tap(isConnected => (this.isConnected = isConnected)),
        filter(isConnected => isConnected), // Only proceed if connected
        switchMap(() =>
          combineLatest([
            this.appService.userBalance$,
            this.appService.currentCurrency$,
            this.depositApi.getTokenNetworks({ category: 'Deposit' }),
          ]),
        ),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        untilDestroyed(this),
      )
      .subscribe(([userBalanceData, currentCurrencyData, tokenNetworksData]) => {
        // 如果是從Bonus頁面Claim進來，優先預設帶入該Bonus和對應的Currency
        this.currentCurrency = this.walletService.targetCurrency || currentCurrencyData?.currency || 'USDT'; // default to USDT

        // Init user balance list data and set current balance
        this.userBalanceList = userBalanceData || [];
        this.setCurrentBalance(this.currentCurrency);
        // Init token networks
        this.initTokenNetworks(tokenNetworksData?.data || []);

        this.isLoading = false;
      });

    this.web3WalletService.walletType$.subscribe(walletType => {
      if (walletType) {
        this.currentWeb3Wallet = walletType;

        // Hide web3 wallet balance for Coinbase Wallet on mobile/tablet devices
        const isMobileDevice =
          this.deviceService.isMobile() ||
          this.deviceService.isTablet() ||
          navigator.userAgent.match(/Android/i) !== null ||
          navigator.userAgent.match(/iPhone|iPad/i) !== null;
        this.isHideWeb3Currency = walletType === Web3WalletType.CoinbaseWallet && isMobileDevice;
      }
    });
  }

  initTokenNetworks(list: TokenNetworksInterface[]) {
    this.tokenNetworkList = list;
    this.currencyOptions = list?.map((item: TokenNetworksInterface) => ({
      label: item.name,
      value: item,
      img: item.icon,
    }));

    // Set default option
    if (this.currencyOptions.length > 0) {
      this.selectedCurrencyOption = list.find(item => item.currency === this.currentCurrency) || list[0];

      this.currentCurrency = this.selectedCurrencyOption.currency;
      this.networkOptions = this.selectedCurrencyOption.networks.map(network => ({
        label: network.desc,
        value: network,
      }));

      this.initNetworkSelect();
    }
  }

  initNetworkSelect() {
    if (this.networkOptions.length > 0) {
      // Set default network option
      if (this.currentWeb3Wallet === Web3WalletType.TonConnect) {
        this.selectedNetworkOption =
          this.networkOptions.find(item => item.value.network === 'TON')?.value || this.networkOptions[0].value;
      } else {
        this.selectedNetworkOption =
          this.networkOptions.find(item => item.value.network === 'ERC20')?.value || this.networkOptions[0].value;
      }

      this.onNetworkSelectChange();
    }
  }

  setCurrentBalance(curCurrency: string) {
    // Set current balance based on selected currency
    this.currentBalance = this.userBalanceList?.find(item => item.currency === curCurrency)?.balance || 0;
  }

  onCurrencySelectChange() {
    if (this.selectedCurrencyOption) {
      this.resetAmount();
      this.currentCurrency = this.selectedCurrencyOption.currency;
      this.setCurrentBalance(this.currentCurrency);

      // Reset network options based on selected currency
      this.networkOptions = [];
      this.selectedNetworkOption = undefined;

      this.networkOptions =
        this.tokenNetworkList
          .find(item => item.currency === this.currentCurrency)
          ?.networks.map(network => ({
            label: network.desc,
            value: network,
          })) || [];

      this.initNetworkSelect();
    }
  }

  async onNetworkSelectChange() {
    // Reset state before switching network
    this.hasNetworkError = false;

    if (this.selectedNetworkOption) {
      this.resetAmount();

      const { chainId, decimals } = this.selectedNetworkOption;
      // Default to 6 decimals if not specified
      this.currentDecimals = decimals || 6;

      // Switch network using Coinbase Wallet service
      await this.web3WalletService.switchNetwork(chainId).catch(() => {
        this.resetAmount();
        this.hasNetworkError = true;

        // 當 Network 不是預設第一個的時候，才切回第一個網路
        if (this.networkOptions.findIndex(item => item.value.chainId === chainId) !== 0) {
          this.initNetworkSelect();
        }
      });

      if (!this.hasNetworkError) {
        // Fetch web3 wallet balance and deposit max amount after network switch
        await this.fetchWeb3WalletBalance();

        // Fetch deposit address
        this.fetchDepositAddress();
      }
    }
  }

  fetchDepositAddress() {
    if (this.isConnected && this.selectedCurrencyOption && this.selectedNetworkOption) {
      this.isAddressLoading = true;

      this.depositApi
        .getDepositAddress({
          currency: this.selectedCurrencyOption.currency,
          network: this.selectedNetworkOption.network,
        })
        .subscribe(res => {
          this.depositAddress = res?.data?.address || '';
          this.isAddressLoading = false;
        });
    }
  }

  async fetchWeb3WalletBalance() {
    if (this.isConnected && this.selectedCurrencyOption && this.selectedNetworkOption) {
      this.isWeb3WalletLoading = true;

      const { contractAddress } = this.selectedNetworkOption;

      // Clear previous current web3 wallet balance and previous max amount
      this.currentWeb3WalletBalance = '0';
      this.depositMaxAmount = '0';

      // Fetch balance and check if contract address
      if (contractAddress) {
        this.currentWeb3WalletBalance =
          (await this.web3WalletService.fetchContractBalance(this.selectedNetworkOption)) || '0';
      } else {
        // Handle non-contract address data
        this.currentWeb3WalletBalance = (await this.web3WalletService.fetchBalance()) || '0';
      }

      // Get web3 wallet balance as max amount
      this.depositMaxAmount = this.currentWeb3WalletBalance;

      // Compare with max amount of selected network option
      const { maxAmount } = this.selectedNetworkOption;
      if (maxAmount < Number(this.depositMaxAmount)) {
        this.depositMaxAmount = maxAmount.toString();
      }

      // Format deposit max amount to string, convert '0.0' to '0'
      this.currentWeb3WalletBalance = parseFloat(this.currentWeb3WalletBalance).toString();
      this.depositMaxAmount = parseFloat(this.depositMaxAmount).toString();

      this.isWeb3WalletLoading = false;
    }
  }

  onSetDepositMaxAmount() {
    this.amount = this.depositMaxAmount;
  }

  getDepositMaxAmount() {
    return Number(this.depositMaxAmount);
  }

  resetAmount() {
    this.amount = '';
    this.currentWeb3WalletBalance = '0';
    this.depositMaxAmount = '0';
    this.currentDecimals = 6;
  }

  isAllLoading() {
    return this.isLoading || this.isBonusLoading || this.isAddressLoading || this.isWeb3WalletLoading;
  }

  isReadyToDeposit() {
    if (this.isAllLoading()) {
      return true;
    }

    return (
      this.isConnected &&
      !this.hasNetworkError &&
      this.selectedCurrencyOption !== undefined &&
      this.selectedNetworkOption !== undefined &&
      this.depositAddress !== '' &&
      Number(this.amount) > 0 &&
      this.amountValid
    );
  }

  async onWeb3Deposit() {
    if (this.isConnected && this.selectedNetworkOption) {
      this.walletService.pushInitiateDepositOnce({
        type: 'web3-wallet',
        currency: this.selectedCurrencyOption?.currency,
        amount: this.amount,
      });

      const { contractAddress } = this.selectedNetworkOption;

      // Start transaction
      this.isTransacting = true;

      // Send transaction and check if contract address
      let hashMsg = '';

      if (contractAddress) {
        // Send transaction with contract data
        hashMsg =
          (await this.web3WalletService.sendContractTransaction(
            this.depositAddress,
            this.amount,
            this.selectedNetworkOption,
          )) || '';
      } else {
        // Handle non-contract address data
        hashMsg =
          (await this.web3WalletService.sendMainTransaction(
            this.depositAddress,
            this.amount,
            this.selectedNetworkOption,
          )) || '';
      }

      // End transaction
      this.isTransacting = false;

      if (hashMsg) {
        this.showDepositDetails(hashMsg);
        this.resetAmount();
      }
    }
  }

  showDepositDetails(msg: string) {
    this.walletService.walletDetailsData = {
      status: 'success',
      title: 'Transfer between wallets has been initiated',
      content: `You can track its progress in the transactions page`,
      typeTitle: 'Deposit',
      data: {
        hashMsg: msg,
        amount: this.amount,
        currency: this.selectedCurrencyOption.currency,
      },
      orderStatus: 'pending',
    };
    this.walletService.setWalletDialogType(WalletDialogType.DEPOSIT_TRANSACTION_DETAILS);
  }

  /**
   * 金額輸入的綁定
   *
   */
  async onAmountInput() {
    // 完全不輸入金額時也不要顯示錯誤訊息
    if (this.amount === '') {
      this.amountValid = true;
      this.errText = '';
      return;
    }

    if (!this.selectedNetworkOption) {
      this.errText = 'Please select a network';
      this.amountValid = false;
      return;
    }

    // 檢查輸入金額是否小於單筆最小存款金額
    if (Number(this.amount) < this.selectedNetworkOption?.minAmount) {
      this.errText = 'The deposit amount is below the minimum limit';
      this.amountValid = false;
      return;
      // 檢查輸入金額是否大於單筆最大存款金額
    } else if (Number(this.amount) > this.selectedNetworkOption?.maxAmount) {
      this.errText = 'The deposit amount exceeds the maximum limit';
      this.amountValid = false;
      return;
    }

    if (this.userAssetsService.dpRemainingAmount$.value !== null) {
      const rateData = await firstValueFrom(this.walletApi.getRate(this.currentCurrency, 'EUR'));
      const remaining = this.userAssetsService.dpRemainingAmount$.value * (rateData?.rates?.[0].rate || 0);

      if (+this.amount > remaining) {
        const remainingText = this.currencyValuePipe.transform(remaining, this.currentCurrency);
        this.errText = `Your remaining deposit limit is ${remainingText} ${this.currentCurrency}`;
        this.amountValid = false;
        this.cdr.markForCheck();
        return;
      }
    }

    this.amountValid = true;
    this.errText = '';
  }

  /**
   * 存款前先判断是否禁止存款
   */
  async checkDepositForbid() {
    this.isForbidLoading = true;
    this.isDepositForbid = await firstValueFrom(
      this.walletApi.forbidPaymentInfo({
        category: ForbidPaymentCategory.Deposit,
        depositType: ForbidDepositType.Digital,
      }),
    );
    this.isForbidLoading = false;
  }
}
